import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { HoldingsResponse } from '../types/api';
import { RefreshCw, Wallet, PieChart, TrendingUp } from 'lucide-react';

export function Holdings() {
  const [holdings, setHoldings] = useState<HoldingsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const holdingsData = await api.getHoldings();
      setHoldings(holdingsData);
    } catch (error) {
      console.error('Error loading holdings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`;

  // Sort positions by weight in descending order
  const sortedPositions = holdings?.positions
    ? [...holdings.positions].sort((a, b) => (b.weight || 0) - (a.weight || 0))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading holdings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Holdings</h1>
          <p className="text-gray-600 mt-1 text-base">Current account positions and balances</p>
        </div>
        <button onClick={loadData} className="inline-flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold rounded-lg bg-emerald-700 text-white shadow-md hover:bg-emerald-800 transition-all">
          <RefreshCw className="w-5 h-5" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Total Equity</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {holdings ? formatCurrency(holdings.total_equity) : '--'}
              </p>
            </div>
            <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-emerald-700 shadow-md">
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Cash</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {holdings && holdings.total_cash != null ? formatCurrency(holdings.total_cash) : '--'}
              </p>
            </div>
            <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-emerald-600 shadow-md">
              <Wallet className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Positions Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {holdings ? formatCurrency(holdings.positions_value) : '--'}
              </p>
            </div>
            <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-emerald-700 shadow-md">
              <PieChart className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-bold text-gray-900">Positions</h2>
        </div>
        
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Symbol</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Market Value</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Weight %</th>
              </tr>
            </thead>
            <tbody>
              {sortedPositions.map((pos, idx) => {
                const currentWeight = pos.weight || 0;

                return (
                  <tr key={pos.symbol} className={`border-b border-gray-200 transition-all duration-200 hover:bg-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                    <td className="px-6 py-5 font-bold text-gray-900 text-base">{pos.symbol}</td>
                    <td className="px-6 py-5 text-right text-base text-gray-700 font-semibold">{pos.quantity}</td>
                    <td className="px-6 py-5 text-right text-base text-gray-700">{formatCurrency(pos.market_value)}</td>
                    <td className="px-6 py-5 text-right text-base text-gray-700">{formatPercent(currentWeight)}</td>
                  </tr>
                );
              })}
              {sortedPositions.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    No positions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-200">
          {sortedPositions.map((pos) => {
            const currentWeight = pos.weight || 0;

            return (
              <div key={pos.symbol} className="p-5 space-y-3 hover:bg-gray-50 transition-all">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900 text-xl">{pos.symbol}</span>
                  <span className="font-bold text-gray-700 text-sm">{formatCurrency(pos.market_value)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Qty: {pos.quantity}</span>
                  <span className="text-gray-600 font-medium">Weight: {formatPercent(currentWeight)}</span>
                </div>
              </div>
            );
          })}
          {sortedPositions.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No positions found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
